/** The fixed Document Type choices. Kept in step with the backend's list. */
export const DOCUMENT_TYPES: string[] = [
  'Contract', 'Invoice', 'Purchase Order', 'Receipt', 'Policy', 'Report', 'Meeting Notes', 'Budget', 'Proposal',
  'Presentation', 'Image', 'Other'
];

export type TagKey = 'documentType' | 'organization' | 'project' | 'year';

export interface ITagSet {
  documentType?: string;
  organization?: string;
  project?: string;
  year?: number;
}

/** A library column the tags are written to. */
export interface ITagColumn {
  key: TagKey;
  /** Internal name used when the app creates the column. */
  name: string;
  displayName: string;
  type: 'choice' | 'text' | 'number';
}

export const TAG_COLUMNS: ITagColumn[] = [
  { key: 'documentType', name: 'DocumentType', displayName: 'Document Type', type: 'choice' },
  { key: 'organization', name: 'Organization', displayName: 'Organization', type: 'text' },
  { key: 'project', name: 'Project', displayName: 'Project', type: 'text' },
  { key: 'year', name: 'Year', displayName: 'Year', type: 'number' }
];

/** AI-proposed tags for one file. */
export interface ITagProposal {
  fileId: string;
  tags: ITagSet;
  confidence: number;
  reason: string;
}

export interface ITagRequest {
  fileId: string;
  name: string;
  extension: string;
  folderPath: string;
}

/** Names already used in the library, so the AI spells the same organization or project the same way. */
export interface IKnownNames {
  organizations: string[];
  projects: string[];
}

export interface IAiTaggingService {
  readonly creditsPerFile: number;
  suggestTags(driveId: string, requests: ITagRequest[], known?: IKnownNames): Promise<{ proposals: ITagProposal[]; creditsUsed: number; balance?: number }>;
}

export interface IColumnInfo {
  /** Needed to change the column (Graph column id). */
  id?: string;
  name: string;
  displayName: string;
  type: 'choice' | 'text' | 'number' | 'other';
  readOnly: boolean;
  /** For a choice column: its options, and whether it also takes values outside them. */
  choices?: string[];
  allowTextEntry?: boolean;
}

/** Library columns and item values. Implemented over Microsoft Graph in the web app, in memory for tests. */
export interface ILibraryMetadata {
  getColumns(): Promise<IColumnInfo[]>;
  createColumn(column: ITagColumn, choices?: string[]): Promise<void>;
  /** Replaces a choice column's options (callers pass the old ones plus the new). */
  setChoices(column: IColumnInfo, choices: string[]): Promise<void>;
  getFields(itemId: string): Promise<{ [column: string]: unknown }>;
  setFields(itemId: string, fields: { [column: string]: unknown }): Promise<void>;
}

/** True when a column already holds a value someone (or something) set. */
export function hasValue(value: unknown): boolean {
  return value !== undefined && value !== null && String(value).trim() !== '';
}

function compatible(column: IColumnInfo, wanted: ITagColumn): boolean {
  if (column.readOnly) {
    return false;
  }
  switch (wanted.type) {
    case 'number': return column.type === 'number' || column.type === 'text';
    case 'choice': return column.type === 'choice' || column.type === 'text';
    default: return column.type === 'text' || column.type === 'choice';
  }
}

/**
 * Which column each tag goes to: an existing column with the same name or display name if
 * its type fits (never a read-only one), otherwise one the app creates. If the natural name
 * is taken by an unsuitable column, the app's column gets a distinct name instead.
 */
export function planColumns(existing: IColumnInfo[]): { [key in TagKey]: { column: ITagColumn; exists: boolean } } {
  const lower = (s: string): string => s.toLowerCase().replace(/\s+/g, '');
  const result = {} as { [key in TagKey]: { column: ITagColumn; exists: boolean } };
  for (const wanted of TAG_COLUMNS) {
    const sameName: IColumnInfo | undefined = existing.filter(c =>
      lower(c.name) === lower(wanted.name) || lower(c.displayName) === lower(wanted.displayName))[0];
    if (sameName && compatible(sameName, wanted)) {
      result[wanted.key] = { column: { ...wanted, name: sameName.name, displayName: sameName.displayName }, exists: true };
    } else if (sameName) {
      result[wanted.key] = { column: { ...wanted, name: `SPO${wanted.name}`, displayName: `${wanted.displayName} (Organizer)` }, exists: false };
    } else {
      result[wanted.key] = { column: wanted, exists: false };
    }
  }
  return result;
}
